import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

import { InputField } from '../components/InputField';
import SelectField from '../components/SelectField';
import TextAreaField from '../components/TextAreaField';
import UploadField from '../components/UploadField';
import RadioField from '../components/RadioField';
import CheckboxField from '../components/CheckboxField';
import CustomDatePicker from '../components/CustomDatePicker';
import WiziwigField from '../components/WiziwigField';
import Modal from '../components/Modal';

import { AppContext } from '../../App';
import { generateId } from '../../functions/generate-id';
import { useForm, useFieldArray } from 'react-hook-form';
import { toTimeSpan } from '../../functions/to-time-span';

const Sidebar = ({ action, courseId, activeSection, setActiveSection }) => {
  return (
    <div className='sidebar w-208 bg-ui h-full py-4 text-lg'>
      <ul>
        <li>
          <Link
            className={`py-2 px-4 hover:bg-uiactive cursor-pointer flex ${
              activeSection === 'details' ? 'bg-uiactive' : ''
            }`}
            to={
              action === 'edit'
                ? `../course/${action}/${courseId}/details`
                : `../course/add/details`
            }
            onClick={() => setActiveSection('details')}
          >
            Course Details
          </Link>
        </li>
        <li>
          <Link
            className={`py-2 px-4 hover:bg-uiactive cursor-pointer flex ${
              activeSection === 'prices' ? 'bg-uiactive' : ''
            }`}
            to={
              action === 'edit'
                ? `../course/${action}/${courseId}/prices`
                : `../course/add/prices`
            }
            onClick={() => setActiveSection('prices')}
          >
            Prices
          </Link>
        </li>
        <li>
          <Link
            className={`py-2 px-4 hover:bg-uiactive cursor-pointer flex ${
              activeSection === 'modules' ? 'bg-uiactive' : ''
            }`}
            to={
              action === 'edit'
                ? `../course/${action}/${courseId}/modules`
                : `../course/add/modules`
            }
            onClick={() => setActiveSection('modules')}
          >
            Modules
          </Link>
        </li>
        <li>
          <Link
            className={`py-2 px-4 hover:bg-uiactive cursor-pointer flex ${
              activeSection === 'entry-criteria' ? 'bg-uiactive' : ''
            }`}
            to={
              action === 'edit'
                ? `../course/${action}/${courseId}/entry-criteria`
                : `../course/add/entry-criteria`
            }
            onClick={() => setActiveSection('entry-criteria')}
          >
            Entry Criteria
          </Link>
        </li>
        <li>
          <Link
            className={`py-2 px-4 hover:bg-uiactive cursor-pointer flex ${
              activeSection === 'access' ? 'bg-uiactive' : ''
            }`}
            to={
              action === 'edit'
                ? `../course/${action}/${courseId}/access`
                : `../course/add/access`
            }
            onClick={() => setActiveSection('access')}
          >
            Visibility & Access
          </Link>
        </li>
        {action === 'edit' && (
          <li>
            <Link
              className={`py-2 px-4 hover:bg-uiactive cursor-pointer flex ${
                activeSection === 'students' ? 'bg-uiactive' : ''
              }`}
              to={`../course/${action}/${courseId}/students`}
              onClick={() => setActiveSection('students')}
            >
              Students
            </Link>
          </li>
        )}
      </ul>
    </div>
  );
};

const CourseDetails = ({
  isSubmitting,
  formData,
  register,
  errors,
  handleSubmit,
  onSubmit,
  setValue,
  watch,
}) => {
  return (
    <div className='h-full text-sm p-6 w-full'>
      <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
        Course details
        <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputField
          label='Course prefix'
          name='coursePrefix'
          register={register}
          rules={{ required: 'Course prefix is required' }}
          error={errors.coursePrefix?.message}
          explanation='This will be prefixed to the student id'
        />
        <InputField
          label='Course title'
          name='courseTitle'
          register={register}
          rules={{ required: 'Course title is required' }}
          error={errors.courseTitle?.message}
        />
        <UploadField
          label='Course logo'
          name='courseLogoUpload'
          register={register}
          setValue={setValue}
          defaultFile={watch('courseLogo')}
        />
        <TextAreaField
          label='Course summary'
          name='courseSummary'
          register={register}
          rules={{ required: 'Course summary is required' }}
          error={errors.courseSummary?.message}
        />
        <WiziwigField
          label='Course introduction'
          name='courseIntroduction'
          defaultValue={formData.courseIntroduction}
          setValue={setValue}
          rules={{ required: 'Course introduction is required' }}
          error={errors.courseIntroduction?.message}
        />
        <WiziwigField
          label='Course updates'
          name='courseUpdates'
          defaultValue={formData.courseUpdates}
          setValue={setValue}
        />
        <TextAreaField
          label='Course video (canva)'
          name='courseVideo'
          register={register}
        />
        <UploadField
          label='Course brochure'
          name='courseBrochureUpload'
          register={register}
          setValue={setValue}
          defaultFile={formData.courseBrochure}
        />
        <UploadField
          label='Course brochure cover image'
          name='courseBrochureCoverImageUpload'
          register={register}
          setValue={setValue}
          defaultFile={formData.courseBrochureCoverImage}
        />
        <TextAreaField
          label='Course brochure summary'
          name='courseBrochureSummary'
          register={register}
        />
        <UploadField
          label='Course handbook'
          name='courseHandbookUpload'
          register={register}
          setValue={setValue}
          defaultFile={formData.courseHandbook}
        />
        <UploadField
          label='Course handbook cover image'
          name='courseHandbookCoverImageUpload'
          register={register}
          setValue={setValue}
          defaultFile={formData.courseHandbookCoverImage}
        />
        <TextAreaField
          label='Course handbook summary'
          name='courseHandbookSummary'
          register={register}
        />
        <CustomDatePicker
          name='courseEnrollmentStartDate'
          label='Enrolment start date'
          selectedDate={watch('courseEnrollmentStartDate')}
          setValue={setValue}
        />
        <CustomDatePicker
          name='courseEnrollmentEndDate'
          label='Enrolment end date'
          selectedDate={watch('courseEnrollmentEndDate')}
          setValue={setValue}
        />
        <CustomDatePicker
          name='courseStartDate'
          label='Course start date'
          selectedDate={watch('courseStartDate')}
          setValue={setValue}
        />
        <CustomDatePicker
          name='courseEndDate'
          label='Course end date'
          selectedDate={watch('courseEndDate')}
          setValue={setValue}
        />
        <CustomDatePicker
          name='courseEndDateYear2'
          label='Course end date year 2'
          selectedDate={watch('courseEndDateYear2')}
          setValue={setValue}
        />
        <WiziwigField
          label='Terms and conditions'
          name='courseTerms'
          defaultValue={formData.courseTerms}
          setValue={setValue}
        />
        <h2 className='text-uidarker font-bold pr-6 text-md relative flex mb-6'>
          Education
        </h2>
        <SelectField
          label='How many to disclose?'
          name='courseEducation'
          options={[0, 1, 2, 3]}
          register={register}
        />
        <h2 className='text-uidarker font-bold pr-6 text-md relative flex mb-6'>
          Professional Qualifications
        </h2>
        <SelectField
          label='How many to disclose?'
          name='courseQualifications'
          options={[0, 1, 2, 3]}
          register={register}
        />
        <h2 className='text-uidarker font-bold pr-6 text-md relative flex mb-6'>
          Course Administrator
        </h2>
        <InputField
          label='Course administrator name'
          name='courseAdminName'
          register={register}
          rules={{ required: 'Course admin is required' }}
          error={errors.courseAdminName?.message}
        />
        <InputField
          label='Course administrator email'
          name='courseAdminEmail'
          register={register}
          rules={{ required: 'Course admin email is required' }}
          explanation={
            "Course administrator's will receive email notifications for course applications."
          }
          error={errors.courseAdminEmail?.message}
        />
        <span className='flex w-full h-0.5 bg-ui mt-6 mb-6'></span>
        <button
          type='submit'
          disabled={isSubmitting}
          className={`${
            isSubmitting ? 'bg-secondary-200' : 'bg-kenticoblue'
          } hover:bg-blue-700 text-white font-medium py-2 px-6 mb-6 rounded-full`}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </form>
    </div>
  );
};

const Prices = ({
  isSubmitting,
  formData,
  register,
  setValue,
  watch,
  countries,
  errors,
  handleSubmit,
  onSubmit,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountries, setSelectedCountries] = useState(
    formData.countryDiscounts,
  );
  const [filteredCountries, setFilteredCountries] = useState(countries);
  const appContext = useContext(AppContext);
  const [isCustomQuestionRequired, setIsCustomQuestionRequired] =
    useState(false);
  const countryDiscounts = watch('countryDiscounts');
  console.log(isCustomQuestionRequired);

  useEffect(() => {
    if (formData.courseQuestionDiscount > 0) {
      setIsCustomQuestionRequired(true);
    }
  }, [formData.courseQuestionDiscount]);

  const handleCustomQuestionRequired = (e) => {
    setIsCustomQuestionRequired(e.target.checked);
    console.log(isCustomQuestionRequired);

    if (!e.target.checked) {
      setValue('courseQuestionDiscount', null);
      setValue('courseQuestionDiscountContent', null);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value.toLowerCase());
    const filteredCountries = countries.filter((country) =>
      country.countryDisplayName.toLowerCase().startsWith(value.toLowerCase()),
    );
    setFilteredCountries(filteredCountries);
  };

  const handleCheckboxChange = (value) => {
    const [courseId, courseDiscountedCountryRate] = watch([
      'courseId',
      'courseDiscountedCountryRate',
    ]);

    let changeIndex = -1;
    const changedCountry = countryDiscounts.find((countryDiscount, index) => {
      if (countryDiscount.countryId == value) {
        changeIndex = index;
        return true;
      }
      return false;
    });

    if (changeIndex !== -1 && changedCountry.deleted != true) {
      // Checkbox is already selected, remove it
      countryDiscounts[changeIndex].deleted = true;
    } else if (changeIndex !== -1 && changedCountry.deleted) {
      countryDiscounts[changeIndex].deleted = false;
    } else {
      if (!countryDiscounts) countryDiscounts = [];
      // Checkbox is not selected, add it
      countryDiscounts.push({
        courseId: courseId,
        countryId: parseInt(value),
        discountedPrice: courseDiscountedCountryRate,
      });
    }

    setSelectedCountries([...countryDiscounts]);
  };

  const refreshCountryDiscountPrices = () => {
    const courseDiscountedCountryRate = watch('courseDiscountedCountryRate');

    countryDiscounts.forEach(function (cd, index) {
      cd.discountedPrice = courseDiscountedCountryRate;
    });
  };

  return (
    <div className='h-full text-sm p-6 w-full'>
      <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
        Prices
        <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputField
          label='Course price'
          name='coursePrice'
          register={register}
          rules={{ required: 'Course price is required' }}
          error={errors.coursePrice?.message}
        />
        <InputField
          label='Member discount'
          name='courseMemberDiscount'
          explanation='Percentage discount for members.'
          register={register}
        />
        <CheckboxField
          labelLeft='Include a custom discount question?'
          type='checkbox'
          checked={isCustomQuestionRequired}
          onChange={handleCustomQuestionRequired}
        />
        {isCustomQuestionRequired && (
          <>
            <TextAreaField
              label='Custom question'
              name='courseQuestionDiscountContent'
              register={register}
              explanation='This yes or no question will be asked to the applicant to determine if they are eligible for a percentage discount.'
            />
            <InputField
              label='Custom question discount'
              name='courseQuestionDiscount'
              register={register}
            />
          </>
        )}
        <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
          Discounted Countries
          <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
        </h2>
        <InputField
          label='Discounted country course price'
          name='courseDiscountedCountryRate'
          onBlur={refreshCountryDiscountPrices}
          explanation='Percentage discount for discounted countries.'
          register={register}
        />
        <InputField
          label='Discounted countries'
          name='courseDiscountedCountries'
          placeholder='Search Countries'
          onChange={(e) => handleSearch(e.target.value)}
          value={searchTerm}
        />
        <div className='mb-3 flex items-start w-full'>
          <label className='w-1/6 text-uidarker font-medium pr-6 text-right'></label>
          <div className='bg-uilight border-solid border-2 border-uidark rounded-lg p-2 h-56 w-full max-w-xs focus:bg-white overflow-y-auto'>
            {filteredCountries.map((country) => {
              const isSelected =
                countryDiscounts.find(
                  (countryDiscount) =>
                    !countryDiscount.deleted &&
                    countryDiscount.countryId == country.countryId,
                ) != null;
              return (
                <CheckboxField
                  key={country.countryId}
                  name={'discountCountry' + country.countryId}
                  label={country.countryDisplayName}
                  type='checkbox'
                  value={country.countryId}
                  checked={isSelected}
                  onChange={(e) => handleCheckboxChange(e.target.value)}
                />
              );
            })}
          </div>
        </div>
        <span className='flex w-full h-0.5 bg-ui mt-6 mb-6'></span>
        <button
          type='submit'
          disabled={isSubmitting}
          className={`${
            isSubmitting ? 'bg-secondary-200' : 'bg-kenticoblue'
          } hover:bg-blue-700 text-white font-medium py-2 px-6 mb-6 rounded-full`}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </form>
    </div>
  );
};

const Modules = ({
  isSubmitting,
  formData,
  register,
  watch,
  errors,
  control,
  handleSubmit,
  onSubmit,
  handleLinkClickAddModuleGroup,
  handleLinkClickAddModule,
  setValue,
}) => {
  const [isMultiYearCourse, setIsMultiYearCourse] = useState(false);

  const moduleGroups = watch('moduleGroups');

  // Sort modules by moduleOrder
  const sortedModules = (modules) =>
    [...modules].sort((a, b) => a.moduleOrder - b.moduleOrder);

  const updateModuleOrder = (group) => {
    group.modules.forEach((module, index) => {
      module.moduleOrder = index + 1;
      module.moduleIndex = index + 1;
    });
    setValue('moduleGroups', [...moduleGroups]);
  };

  const handleMoveModule = (groupId, moduleIndex, direction) => {
    const group = moduleGroups.find((g) => g.tempId === groupId);
    if (!group) return;

    const modules = [...group.modules];
    if (direction === 'up' && moduleIndex > 0) {
      [modules[moduleIndex - 1], modules[moduleIndex]] = [
        modules[moduleIndex],
        modules[moduleIndex - 1],
      ];
    } else if (direction === 'down' && moduleIndex < modules.length - 1) {
      [modules[moduleIndex + 1], modules[moduleIndex]] = [
        modules[moduleIndex],
        modules[moduleIndex + 1],
      ];
    }

    group.modules = modules;
    //setValue("moduleGroups", [...moduleGroups]);
    updateModuleOrder(group);
  };

  const handleMultiYearCourse = (e) => {
    setIsMultiYearCourse(e.target.checked);
  };

  // Function to handle removing a module group
  const handleRemoveModuleGroup = (groupId) => {
    var moduleGroup = moduleGroups.find((group) => group.tempId == groupId);
    if (!moduleGroup) return;
    moduleGroup.deleted = true;
    setValue('moduleGroups', [...moduleGroups]);
  };

  // Function to handle removing a module
  const handleRemoveModule = (groupId, moduleId) => {
    var moduleGroup = moduleGroups.find((group) => group.tempId == groupId);
    if (!moduleGroup) return;
    const module = moduleGroup.modules.find(
      (module) => module.tempId === moduleId,
    );
    if (!module) return;
    module.deleted = true;
    setValue('moduleGroups', [...moduleGroups]);
  };

  return (
    <div className='h-full text-sm p-6 w-full'>
      <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
        Modules
        <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CheckboxField
          labelLeft='All modules are required'
          name='allModulesRequired'
          register={register}
          onChange={(e) => setValue('allModulesRequired', e.target.checked)}
        />
        <CheckboxField
          labelLeft='Can this course be taken over multiple years?'
          name='multiYearCourse'
          onChange={handleMultiYearCourse}
        />
        {isMultiYearCourse && (
          <SelectField
            label='How many years?'
            name='yearsToComplete'
            options={['1', '2']}
            register={register}
          />
        )}
        {!watch('allModulesRequired') && (
          <>
            <InputField
              label='Minimum modules required'
              name='courseMinModules'
              register={register}
            />
            <InputField
              label='Elective modules required'
              name='courseMinElectiveModules'
              value={formData.courseMinElectiveModules}
              register={register}
            />
            <InputField
              label='Price per module'
              name='courseAdditionalPricePerModule'
              value={formData.courseAdditionalPricePerModule}
              register={register}
            />
            <TextAreaField
              label='Modules summary'
              name='applicationModulesSummary'
              register={register}
            />
          </>
        )}
        <h2 className='text-uidarker font-bold pr-6 text-md relative flex mb-6'>
          Module pass mark
        </h2>
        <InputField
          label='The % mark required to pass a module on the course'
          name='courseModulePassMark'
          value={formData.courseModulePassMark}
          register={register}
        />
        <Link
          onClick={() => handleLinkClickAddModuleGroup()}
          className='text-blue-500'
        >
          <button className='bg-kenticoblue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full'>
            Add Module Group
          </button>
        </Link>
        <span className='block w-full h-0.5 bg-ui my-4'></span>
        <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
          Module Groups
          <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
        </h2>
        {moduleGroups && (
          <>
            {moduleGroups
              .filter((mg) => !mg.deleted)
              .map((group, index) => (
                <table key={index} className='table-auto w-full mb-4'>
                  <thead>
                    <tr className='bg-ui'>
                      <th className='px-4 py-2 text-left w-4/5'>
                        {group?.groupName}
                      </th>
                      <td className='px-4 py-2'>
                        <Link
                          onClick={() =>
                            handleLinkClickAddModuleGroup(group.tempId)
                          }
                          className='text-kenticoblue underline'
                        >
                          Edit
                        </Link>
                      </td>
                      <td className='px-4 py-2'>
                        <button
                          type='button'
                          onClick={() => handleRemoveModuleGroup(group.tempId)}
                          className='text-red underline'
                        >
                          Remove
                        </button>
                      </td>
                      <td className='px-4 py-2 whitespace-nowrap'>Move Up</td>
                      <td className='px-4 py-2 whitespace-nowrap'>Move Down</td>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedModules(group.modules)
                      .filter((m) => !m.deleted)
                      .map((module, moduleIndex) => (
                        //{group.modules && group.modules.filter(m => !m.deleted).map((module, moduleIndex) => (
                        <tr
                          key={module.tempId}
                          className='border-b-1 hover:bg-brandGrey'
                        >
                          <td className='px-4 py-2'>{module.moduleName}</td>
                          <td className='px-4 py-2'>
                            <Link
                              onClick={() =>
                                handleLinkClickAddModule(
                                  group.tempId,
                                  module.tempId,
                                )
                              }
                              className='text-kenticoblue underline'
                            >
                              Edit
                            </Link>
                          </td>
                          <td className='px-4 py-2'>
                            <button
                              type='button'
                              onClick={() =>
                                handleRemoveModule(group.tempId, module.tempId)
                              }
                              className='text-red underline'
                            >
                              Remove
                            </button>
                          </td>
                          <td className='px-4 py-2'>
                            <button
                              type='button'
                              onClick={() =>
                                handleMoveModule(
                                  group.tempId,
                                  moduleIndex,
                                  'up',
                                )
                              }
                              disabled={moduleIndex === 0} // Disable if it's the first module
                              className='text-lightHero underline whitespace-nowrap'
                            >
                              Move Up
                            </button>
                          </td>
                          <td className='px-4 py-2'>
                            <button
                              type='button'
                              onClick={() =>
                                handleMoveModule(
                                  group.tempId,
                                  moduleIndex,
                                  'down',
                                )
                              }
                              disabled={
                                moduleIndex === group.modules.length - 1
                              } // Disable if it's the last module
                              className='text-lightHero underline whitespace-nowrap'
                            >
                              Move Down
                            </button>
                          </td>
                        </tr>
                      ))}
                    <tr key='' className='border-b-1'>
                      <td className='px-4 py-2'>
                        <Link
                          onClick={() => handleLinkClickAddModule(group.tempId)}
                          className='text-kenticoblue underline'
                        >
                          Add Module
                        </Link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              ))}
          </>
        )}
        <span className='flex w-full h-0.5 bg-ui mt-6 mb-6'></span>
        <button
          type='submit'
          disabled={isSubmitting}
          className={`${
            isSubmitting ? 'bg-secondary-200' : 'bg-kenticoblue'
          } hover:bg-blue-700 text-white font-medium py-2 px-6 mb-6 rounded-full`}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </form>
    </div>
  );
};

const AddModuleGroup = ({ moduleGroup, onSaved }) => {
  const { register, handleSubmit } = useForm({
    defaultValues: moduleGroup,
  });

  return (
    <div className='h-full text-sm p-6 w-full'>
      <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
        {moduleGroup.isNew ? 'Add Module Group' : 'Edit Module Group'}
        <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
      </h2>
      <form onSubmit={handleSubmit(onSaved)}>
        <InputField
          label='Module group name'
          name='groupName'
          register={register}
        />
        <TextAreaField
          label='Module group summary'
          name='groupSummary'
          register={register}
        />
        <RadioField
          name='requirementCodeName'
          options={[
            { value: 'compulsory', label: 'Compulsory' },
            { value: 'ab-compulsory', label: 'AB Compulsory' },
            { value: 'elective', label: 'Elective' },
          ]}
          register={register}
        />
        <button
          type='submit'
          className='bg-kenticoblue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full'
        >
          Save
        </button>
      </form>
    </div>
  );
};

const AddAssessment = ({ saveAssessment, setAddingAssessment }) => {
  const { register, control, setValue, handleSubmit, watch, formState } =
    useForm({
      defaultValues: {
        assessmentType: 'Formative',
      },
    });

  return (
    <form onSubmit={handleSubmit(saveAssessment)}>
      <InputField
        label='Assessment Name'
        name='assessmentTitle'
        register={register}
      />
      <UploadField
        label='Assessment Upload'
        name='file'
        setValue={setValue}
        register={register}
      />
      <SelectField
        label='Assessment Type'
        name='assessmentType'
        options={['Formative', 'Summative']}
        register={register}
      />
      <CustomDatePicker
        label='Assessment Year 1 Deadline'
        name='year1Deadline'
        selectedDate={watch('year1Deadline')}
        setValue={setValue}
        showTime={true}
      />
      <CustomDatePicker
        label='Assessment Year 2 Deadline'
        name='year2Deadline'
        selectedDate={watch('year2Deadline')}
        setValue={setValue}
        showTime={true}
      />
      <div className='flex justify-end'>
        <button
          onClick={() => {
            setAddingAssessment(false);
          }}
          className='bg-uidark hover:bg-blue-700 text-uidarker font-medium py-2 px-6 mr-3 rounded-full'
        >
          Cancel
        </button>
        <button
          type='submit'
          className='bg-kenticoblue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full'
        >
          Add Assessment
        </button>
      </div>
    </form>
  );
};

const EditAssessment = ({ saveEditAssessment, onCancel, assessmentData }) => {  
  const { register, setValue, handleSubmit, watch } = useForm({
    defaultValues: {
      ...(assessmentData || {}),
    },
  });

  console.log('EditAssessment', assessmentData);

  return (
    <form onSubmit={handleSubmit(saveEditAssessment)}>
      <InputField
        label='Assessment Name'
        name='assessmentTitle'
        register={register}
      />
      <UploadField
        label='Assessment Upload'
        name='file'
        setValue={setValue}
        register={register}
        defaultFile={assessmentData}
      />
      <SelectField
        label='Assessment Type'
        name='assessmentType'
        options={['Formative', 'Summative']}
        register={register}
      />
      <CustomDatePicker
        label='Assessment Year 1 Deadline'
        name='year1Deadline'
        selectedDate={watch('year1Deadline')}
        setValue={setValue}
        showTime={true}
      />
      <CustomDatePicker
        label='Assessment Year 2 Deadline'
        name='year2Deadline'
        selectedDate={watch('year2Deadline')}
        setValue={setValue}
        showTime={true}
      />
      <div className='flex justify-end'>
        <button
          onClick={onCancel}
          className='bg-uidark hover:bg-blue-700 text-uidarker font-medium py-2 px-6 mr-3 rounded-full'
        >
          Cancel
        </button>
        <button
          type='submit'
          className='bg-kenticoblue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full'
        >
          Edit Assessment
        </button>
      </div>
    </form>
  );
};

const AddModule = ({ module, moduleGroup, countries, onSaved, onClose }) => {
  const { register, setValue, handleSubmit } = useForm({
    defaultValues: { ...module },
  });
  const [addingAssessment, setAddingAssessment] = useState(false);
  const [editAssessmentData, setEditAssessmentData] = useState(null);
  const [moduleState, setModuleState] = useState(module);
  const [countryOptions, setCountryOptions] = useState([]);
  const appContext = useContext(AppContext);

  if (module.tempId !== moduleState.tempId) {
    setModuleState(module);
  }

  if (!countryOptions.find((c) => c.countryId == 0)) {
    const newCountryOptions = [...countries];
    newCountryOptions.unshift({ countryId: 0, countryDisplayName: 'None' });
    setCountryOptions(newCountryOptions);
  }

  const addAssessment = () => {
    setAddingAssessment(true);
  };

  const editAssessment = (index) => {
    const editAssessmentData = module.assessments[index];
    setEditAssessmentData({ ...editAssessmentData, index });
  };

  const onCanelEditAssessment = () => {
    setEditAssessmentData(null);
  };

  const handleDocumentUploadChange = (event) => {
    const newDoc = {
      file: event.target.info,
    };
    moduleState.documents.push(newDoc);
    setValue('documents', moduleState.documents);
    event.resetFiles();
    setModuleState({ ...moduleState });
  };

  const prepareAssessmentDates = (assessment) => {
    assessment.year1Deadline = ensureDate(assessment.year1Deadline);
    assessment.year2Deadline = ensureDate(assessment.year2Deadline);
    assessment.year1DeadlineTime = toTimeSpan(assessment.year1Deadline);
    assessment.year2DeadlineTime = toTimeSpan(assessment.year2Deadline);
  };

  const saveAssessment = (assessment) => {
    debugger;
    prepareAssessmentDates(assessment);
    module.assessments.push(assessment);
    setValue('assessments', moduleState.assessments);
    setAddingAssessment(false);
  };

  const saveEditAssessment = (assessment) => {
    debugger;
    prepareAssessmentDates(assessment);
    module.assessments[editAssessmentData.index] = assessment;
    setValue('assessments', moduleState.assessments);
    onCanelEditAssessment();
  };

  const removeAssessment = (index, event) => {
    event.preventDefault();
    module.assessments[index].deleted = true;

    setValue('assessments', moduleState.assessments);

    setModuleState({ ...moduleState });
  };

  const removeDocument = async (index, event) => {
    event.preventDefault();

    const doc = module.documents[index];

    if (doc.file.uploadId) {
      await appContext.httpClient.post({
        path: 'fileupload/remove/' + doc.file.uploadId,
      });
    }

    module.documents[index].deleted = true;
    setValue('documents', moduleState.documents);

    setModuleState({ ...moduleState });
  };

  const saveModule = (moduleData) => {
    onSaved(moduleGroup, moduleData);
  };

  const formatDate = (dateString) => {
    const options = { day: 'numeric', month: 'numeric', year: 'numeric' };
    return dateString
      ? new Date(dateString).toLocaleDateString('en-GB', options)
      : '';
  };

  const editingAssessment = Boolean(editAssessmentData !== null);

  if (addingAssessment) {
    return (
      <div className="p-6">
        <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
          Add Assessment
          <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
        </h2>
        <AddAssessment
          saveAssessment={saveAssessment}
          setAddingAssessment={setAddingAssessment}
        />
      </div>
    );
  }

  if (editingAssessment) {
    return (
      <div className="p-6">
        <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
          Edit Assessment
          <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
        </h2>
        <EditAssessment
          saveEditAssessment={saveEditAssessment}
          onCancel={onCanelEditAssessment}
          assessmentData={editAssessmentData}
        />
      </div>
    );
  }

  return (
    <div className='h-full text-sm p-6 w-full'>
      <>
        <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
          {!module.isNew ? 'Edit Module' : 'Add Module'}
          <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
        </h2>
        <form onSubmit={handleSubmit(saveModule)}>
          <InputField label='Name' name='moduleName' register={register} />
          <TextAreaField
            label='Summary'
            name='moduleSummary'
            register={register}
          />
          <WiziwigField
            label='Description'
            name='moduleDescription'
            defaultValue={module.moduleDescription}
            setValue={setValue}
          />
          <CheckboxField
            labelLeft='Face to face assessment'
            name='faceToFaceAssessment'
            register={register}
          />
          <InputField
            label='Video URL'
            name='videoURL'
            //value={moduleState.videoURL}
            //onChange={handleModuleFieldChange}
            register={register}
          />
          <UploadField
            label='Documents'
            onChange={handleDocumentUploadChange}
          />
          {module.documents.length > 0 && (
            <table className='table-auto w-full mb-4'>
              <thead>
                <tr className='bg-ui'>
                  <th className='px-4 py-2 text-left'>Document</th>
                  <th className='px-4 py-2 text-left'></th>
                </tr>
              </thead>
              <tbody>
                {moduleState.documents.map(
                  (document, index) =>
                    !document.deleted && (
                      <tr key={index} className='border-b-1'>
                        <td className='px-4 py-2'>{document.file.filename}</td>
                        <td className='px-4 py-2'>
                          <Link
                            className='text-red underline'
                            onClick={(event) => removeDocument(index, event)}
                          >
                            Remove
                          </Link>
                        </td>
                      </tr>
                    ),
                )}
              </tbody>
            </table>
          )}
          <WiziwigField
            label='Assessment information'
            name='assessmentInformation'
            defaultValue={module.assessmentInformation}
            setValue={setValue}
          />
          <table className='table-auto w-full mb-4'>
            <thead>
              <tr className='bg-ui'>
                <th className='px-4 py-2 text-left'>Assessments</th>
                <th className='px-4 py-2 text-left'>Upload</th>
                <th className='px-4 py-2 text-left'>Type</th>
                <th className='px-4 py-2 text-left'>Year 1 Deadline</th>
                <th className='px-4 py-2 text-left'>Year 2 Deadline</th>
                <th className='px-4 py-2 text-left'>Edit</th>
                <th className='px-4 py-2 text-left'>Remove</th>
              </tr>
            </thead>
            <tbody>
              {moduleState.assessments.map(
                (assessment, index) =>
                  !assessment.deleted && (
                    <tr key={index} className='border-b-1'>
                      <td className='px-4 py-2'>
                        {assessment.assessmentTitle} -{' '}
                        {assessment.courseModuleAssessmentId}{' '}
                      </td>
                      <td className='px-4 py-2'>
                        {assessment.file ? (
                          assessment.fileId > 0
                          ? assessment.file.filename + (assessment.file.fileExtension || "")
                          : assessment.file.filename
                        ) : 'No file uploaded'}
                      </td>
                      <td className='px-4 py-2'>{assessment.assessmentType}</td>
                      <td className='px-4 py-2'>
                        {formatDate(assessment.year1Deadline)}
                      </td>
                      <td className='px-4 py-2'>
                        {formatDate(assessment.year2Deadline)}
                      </td>
                      <td className='px-4 py-2'>
                        <Link
                          className='text-kenticoblue underline'
                          onClick={() => editAssessment(index)}
                        >
                          Edit
                        </Link>
                      </td>
                      <td className='px-4 py-2'>
                        <Link
                          className='text-red underline'
                          onClick={(event) => removeAssessment(index, event)}
                        >
                          Remove
                        </Link>
                      </td>
                    </tr>
                  ),
              )}
              <tr key='' className='border-b-1'>
                <td className='px-4 py-2'>
                  <Link
                    onClick={addAssessment}
                    className='text-kenticoblue underline'
                  >
                    Add Assessment
                  </Link>
                </td>
              </tr>
            </tbody>
          </table>
          <div className='flex justify-end'>
            <button
              onClick={onClose}
              className='bg-uidark hover:bg-blue-700 text-uidarker font-medium py-2 px-6 mr-3 rounded-full'
            >
              Cancel
            </button>
            <button
              type='submit'
              className='bg-kenticoblue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full'
            >
              Save
            </button>
          </div>
        </form>
      </>
    </div>
  );
};

const EntryCriteria = ({
  isSubmitting,
  control,
  watch,
  setValue,
  register,
  handleSubmit,
  onSubmit,
  errors,
}) => {
  const { fields, append } = useFieldArray({
    name: 'entryCriteria',
    control,
  });
  let [courseId] = watch(['courseId']);

  useEffect(() => {
    if (!fields.length) {
      append({ isDouble: false, courseId });
    }
  }, [fields.length, courseId, append]);

  const addCriteria = () => {
    append({ isDouble: false, courseId });
  };
  const removeCriteria = (index) => {
    const entryCriterias = watch('entryCriteria');
    debugger;
    entryCriterias[index].deleted = true;
    setValue('entryCriteria', entryCriterias);
  };

  return (
    <div className='h-full text-sm p-6 w-full'>
      <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
        Entry Criteria
        <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        {fields.map(
          (criteria, index) =>
            !criteria.deleted && (
              <div
                key={index}
                data-id={criteria.courseEntryCriteriaId}
                className='mb-6'
              >
                <div className='flex justify-between items-center max-w-2xl mb-6'>
                  <h2 className='text-uidarker font-bold pr-6 text-md relative flex'>
                    Entry Criteria {index + 1}
                  </h2>
                  {index > 0 && (
                    <button
                      type='button'
                      onClick={() => removeCriteria(index)}
                      className='text-red underline font-medium py-1 px-3 rounded'
                    >
                      Remove
                    </button>
                  )}
                </div>
                <TextAreaField
                  label='Statement'
                  name={`entryCriteria.${index}.statement`}
                  register={register}
                />
                <TextAreaField
                  label='Clarification'
                  name={`entryCriteria.${index}.clarification`}
                  register={register}
                />
                <CheckboxField
                  labelLeft='Does this entry criteria require an upload?'
                  name={`entryCriteria.${index}.fileUploadRequired`}
                  register={register}
                />
                {watch(`entryCriteria.${index}.fileUploadRequired`) && (
                  <CheckboxField
                    labelLeft='Can this entry criteria be sent later?'
                    name={`entryCriteria.${index}.fileCanBeSentLater`}
                    register={register}
                  />
                )}
                <CheckboxField
                  labelLeft='OR'
                  name={`entryCriteria.${index}.isDouble`}
                  register={register}
                />
                {watch(`entryCriteria.${index}.isDouble`) && (
                  <>
                    <TextAreaField
                      label='Statement'
                      name={`entryCriteria.${index}.statement2`}
                      register={register}
                    />
                    <TextAreaField
                      label='Clarification'
                      name={`entryCriteria.${index}.clarification2`}
                      register={register}
                    />
                    <CheckboxField
                      labelLeft='Does this entry criteria require an upload?'
                      name={`entryCriteria.${index}.fileUploadRequired2`}
                      register={register}
                    />
                    {watch(`entryCriteria.${index}.fileUploadRequired2`) && (
                      <CheckboxField
                        labelLeft='Can this entry criteria be sent later?'
                        name={`entryCriteria.${index}.fileCanBeSentLater2`}
                        register={register}
                      />
                    )}
                  </>
                )}
              </div>
            ),
        )}
        <button
          type='button'
          onClick={addCriteria}
          className='bg-kenticoblue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full'
        >
          Add More
        </button>
        <span className='flex w-full h-0.5 bg-ui mt-6 mb-6'></span>
        <button
          type='submit'
          disabled={isSubmitting}
          className={`${
            isSubmitting ? 'bg-secondary-200' : 'bg-kenticoblue'
          } hover:bg-blue-700 text-white font-medium py-2 px-6 mb-6 rounded-full`}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </form>
    </div>
  );
};

const Access = ({
  isSubmitting,
  control,
  watch,
  setValue,
  register,
  handleSubmit,
  onSubmit,
  errors,
}) => {
  return (
    <div className='h-full text-sm p-6 w-full'>
      <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
        Visibility & Access
        <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
      </h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CheckboxField
          labelLeft='Allow access to Fellows'
          name="allowFellowsAccess"
          register={register}
        />
        <span className='flex w-full h-0.5 bg-ui mt-6 mb-6'></span>
        <button
          type='submit'
          disabled={isSubmitting}
          className={`${
            isSubmitting ? 'bg-secondary-200' : 'bg-kenticoblue'
          } hover:bg-blue-700 text-white font-medium py-2 px-6 mb-6 rounded-full`}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </form>
    </div>
  );
};

const Students = ({
  formData,
  register,
  handleInputChange,
  handleSubmit,
  handleAdvancedCourse,
  courseId,
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [students, setStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [searchStudent, setSearchStudent] = useState('');
  const { action } = useParams();
  const appContext = useContext(AppContext);

  // Function to handle date change
  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  useEffect(() => {
    // Map over applicationData and fetch corresponding student data
    appContext.httpClient
      .get({
        path: `course/${courseId}/students`,
        authRedirectPath: appContext.loginPath,
      })
      .then((response) => {
        if (response.success && response.result) {
          setStudents(response.result);
        }
      });
  }, []);

  // Filtering data based on search term
  const filteredData = students
    .filter(
      (item) =>
        item.firstName.toLowerCase().includes(searchStudent.toLowerCase()) ||
        item.lastName.toLowerCase().includes(searchStudent.toLowerCase()),
    )
    .sort((a, b) =>
      (a.lastName?.trim().toLowerCase() || '').localeCompare(
        b.lastName?.trim().toLowerCase() || '',
      ),
    );

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className='h-full text-sm p-6 w-full'>
      <h2 className='text-uidarker font-bold pr-6 text-lg relative flex mb-6'>
        Students
        <span className='absolute bottom-0 left-0 w-full h-0.5 bg-ui'></span>
      </h2>
      <div className='mb-3 flex flex-grow items-center'>
        <label className='w-1/6 text-uidarker font-medium pr-6 text-right'>
          Search:
        </label>
        <input
          type='text'
          placeholder='Search...'
          value={searchStudent}
          onChange={(e) => setSearchStudent(e.target.value)}
          className='bg-uilight border-solid border-2 border-uidark rounded-full py-1 px-4 w-full max-w-xs'
        />
      </div>
      <table className='table-auto w-full'>
        <thead>
          <tr className='bg-ui'>
            <th className='px-4 py-2 text-left'>Student ID</th>
            <th className='px-4 py-2 text-left'>Email</th>
            <th className='px-4 py-2 text-left'>First name</th>
            <th className='px-4 py-2 text-left'>Last name</th>
            <th className='px-4 py-2 text-left'>Edit</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((item) => (
            <tr key={item.courseStudentId} className='border-b-1'>
              <td className='px-4 py-2'>{item.studentId}</td>
              <td className='px-4 py-2'>{item.email}</td>
              <td className='px-4 py-2'>{item.firstName}</td>
              <td className='px-4 py-2'>{item.lastName}</td>
              <td className='px-4 py-2'>
                <Link
                  to={`../course/${courseId}/students/${item.courseStudentId}`}
                  className='text-kenticoblue underline'
                >
                  View
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        itemsPerPage={itemsPerPage}
        totalItems={filteredData.length}
        paginate={paginate}
      />
    </div>
  );
};

const Pagination = ({ itemsPerPage, totalItems, paginate }) => {
  const pageNumbers = [];

  for (let i = 1; i <= Math.ceil(totalItems / itemsPerPage); i++) {
    pageNumbers.push(i);
  }

  return (
    <div className='flex justify-center mt-4'>
      <ul className='flex'>
        {pageNumbers.map((number) => (
          <li key={number}>
            <button
              onClick={() => paginate(number)}
              className='px-3 py-1 text-blue-500'
            >
              {number}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
const ensureDate = (dateInput) =>
  dateInput && typeof dateInput == 'string' ? new Date(dateInput) : dateInput;

const AddCourse = () => {
  const { courseId, section } = useParams();
  const [course, setCourse] = useState({});
  const [action, setAction] = useState('add');
  const [activeSection, setActiveSection] = useState(section || 'details');
  const [showAddModuleGroup, setShowAddModuleGroup] = useState(false);
  const [showAddModule, setShowAddModule] = useState(false);
  const [selectedModuleGroup, setSelectedModuleGroup] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [isCourseRequested, setIsCourseRequested] = useState(false);
  const [countries, setCountries] = useState([]);
  const [isCountriesRequested, setIsCountriesRequested] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const appContext = useContext(AppContext);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (course?.courseId) {
      setFormData({
        courseId: course.courseId,
        courseTitle: course.courseTitle,
        courseLogo: course.courseLogo,
        courseLogoUpload: {
          uploadId: null,
          remove: false,
        },
        courseSummary: course.courseSummary,
        courseIntroduction: course.courseIntroduction,
        courseUpdates: course.courseUpdates,
        courseVideo: course.courseVideo,
        courseBrochure: course.courseBrochure,
        courseBrochureCoverImage: {
          uploadId: null,
          remove: false,
        },
        courseHandbook: course.courseHandbook,
        courseHandbookCoverImage: {
          uploadId: null,
          remove: false,
        },
        courseEnrollmentStartDate: course.courseEnrollmentStartDate || null,
        courseEnrollmentEndDate: course.courseEnrollmentEndDate,
        courseStartDate: course.courseStartDate,
        courseEndDate: course.courseEndDate,
        courseEndDateYear2: course.courseEndDateYear2,
        courseTerms: course.courseTerms,
        coursePrice: course.coursePrice,
        courseMemberDiscount: course.courseMemberDiscount,
        courseQuestionDiscountContent: course.courseQuestionDiscountContent,
        courseQuestionDiscount: course.courseQuestionDiscount,
        courseDiscountedCountryRate: course.courseDiscountedCountryRate,
        countryDiscounts: course.countryDiscounts,
        moduleGroups: course.moduleGroups || [],
        requiresActiveMembership: course.requiresActiveMembership,
        allModulesRequired: course.allModulesRequired,
        courseMinModules: course.courseMinModules,
        courseMinElectiveModules: course.courseMinElectiveModules,
        courseAdditionalPricePerModule: course.courseAdditionalPricePerModule,
        courseModulePassMark: course.courseModulePassMark,
        courseEducation: course.courseEducation,
        courseQualifications: course.courseProfessionalQualification,
        courseAdminName: course.courseAdminName,
        courseAdminEmail: course.courseAdminEmail,
        courseEntryCriteriaStatement: course.courseEntryCriteriaStatement,
        courseEntryCriteriaClarification:
          course.courseEntryCriteriaClarification,
        courseEntryCriteriaRequiresUpload:
          course.courseEntryCriteriaRequiresUpload,
        courseEntryCriteriaSendLater: course.courseEntryCriteriaSendLater,
        entryCriteria: course.entryCriteria,
        allowFellowsAccess: course.allowFellowsAccess,
      });
    }
  }, [course]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      courseAdditionalPricePerModule: null,
      courseEducation: 1,
      courseQualifications: 1,
      courseDiscountedCountryRate: null,
      courseMemberDiscount: null,
      courseMinElectiveModules: null,
      courseMinModules: null,
      courseModulePassMark: null,
      courseEnrollmentStartDate: null,
      courseEnrollmentEndDate: null,
      courseStartDate: null,
      courseEndDate: null,
      courseEndDateYear2: null,
      moduleGroups: [],
      countryDiscounts: [],
    },
  });

  const [moduleGroups] = watch(['moduleGroups']);

  const handleLinkClickAddModuleGroup = (moduleGroupId) => {
    const moduleGroup = moduleGroups.find(
      (group) => group.tempId === moduleGroupId,
    ) || {
      tempId: generateId(),
      isNew: true,
    };
    setSelectedModuleGroup(moduleGroup);
    setShowAddModuleGroup(true);
  };

  const handleLinkClickAddModule = (moduleGroupId, moduleId) => {
    const moduleGroup = moduleGroups.find(
      (group) => group.tempId === moduleGroupId,
    );

    if (!moduleGroup) return;

    const maxOrder = moduleGroup.modules
      .filter((m) => !m.deleted) // Exclude deleted modules
      .reduce((max, module) => Math.max(max, module.moduleOrder || 0), 0);

    const module = moduleGroup.modules.find(
      (module) => module.tempId === moduleId,
    ) || {
      tempId: generateId(),
      isNew: true,
      moduleOrder: maxOrder + 1, // Set the next available module order
      assessments: [],
      documents: [],
    };

    setSelectedModuleGroup(moduleGroup);
    setSelectedModule(module);
    setShowAddModule(true);
  };

  const handleCloseModuleGroupModal = () => setShowAddModuleGroup(false);
  const handleCloseModuleModal = () => setShowAddModule(false);

  useEffect(() => {
    console.log('showAddModuleGroup state changed:', showAddModuleGroup);
  }, [showAddModuleGroup]);

  useEffect(() => {
    if (courseId > 0 && !isCourseRequested) {
      setIsCourseRequested(true);
      appContext.httpClient
        .get({
          path: 'course/' + courseId,
          authRedirectPath: appContext.loginPath,
        })
        .then((response) => {
          if (response.result) {
            console.log(response.result);
            const courseData = response.result;
            courseData.courseStartDate = ensureDate(courseData.courseStartDate);
            courseData.courseEndDate = ensureDate(courseData.courseEndDate);
            courseData.courseEndDateYear2 = ensureDate(courseData.courseEndDateYear2);
            courseData.courseEnrollmentStartDate = ensureDate(
              courseData.courseEnrollmentStartDate,
            );
            courseData.courseEnrollmentEndDate = ensureDate(
              courseData.courseEnrollmentEndDate,
            );

            if (Array.isArray(courseData.moduleGroups)) {
              courseData.moduleGroups.forEach((group) => {
                group.tempId = generateId();
                if (!Array.isArray(group.modules)) {
                  group.modules = [];
                }
                group.modules.forEach((module) => {
                  module.tempId = generateId();
                  if (!Array.isArray(module.assessments)) {
                    module.assessments = [];
                  }
                  if (!Array.isArray(module.documents)) {
                    module.documents = [];
                  }
                  module.assessments.forEach((assessment) => {
                    debugger;
                    assessment.year1Deadline = ensureDate(
                      assessment.year1DeadlineDateTime,
                    );
                    assessment.year2Deadline = ensureDate(
                      assessment.year2DeadlineDateTime,
                    );
                  });
                });
              });
            }

            setCourse(courseData);
            setAction('edit');
            reset(courseData);
          }
          setIsNew(false);
        });
    }

    if (!isCountriesRequested) {
      appContext.httpClient
        .get({
          path: 'country',
          authRedirectPath: appContext.loginPath,
        })
        .then((response) => {
          if (response.result) {
            setCountries(response.result);
          }
        });
      setIsCountriesRequested(true);
    }
  }, [courseId, reset]);

  /*
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPropertyValue(formData, name, value);
        setFormData({ ...formData });
    };
    */

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      console.log(action);
      console.log('onSubmit');
      console.log(data);

      if (data.coursePrice === '') data.coursePrice = null;
      if (data.courseMemberDiscount === '') data.courseMemberDiscount = null;
      if (data.courseQuestionDiscount === '')
        data.courseQuestionDiscount = null;
      if (data.courseDiscountedCountryRate === '')
        data.courseDiscountedCountryRate = null;
      if (data.courseAdditionalPricePerModule === '')
        data.courseAdditionalPricePerModule = null;

      let response;
      response = await appContext.httpClient.post({
        path: 'course',
        body: data,
      });

      if (response.success) {
        setShowSuccessModal(true);
        reset(response.result); // Reset form with the response data
      }
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false); // Re-enable the button after submission
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    window.location.reload();
  };

  const saveModuleGroup = (moduleGroup) => {
    if (!moduleGroup) {
      console.log('moduleGroup is not set.');
    }

    if (moduleGroup.isNew) {
      moduleGroup.tempId = generateId();
      moduleGroup.courseId = courseId;
      moduleGroup.isNew = false;
      moduleGroup.modules = [];
      moduleGroups.push(moduleGroup);
    } else {
      let index = -1;
      moduleGroups.find((group, i) => {
        if (group.tempId === moduleGroup.tempId) {
          index = i;
          return true;
        }
        return false;
      });
      if (index > -1) {
        moduleGroups[index] = moduleGroup;
      }
    }

    setShowAddModuleGroup(false);
  };

  const saveModule = (moduleGroup, module) => {
    if (!moduleGroup) {
      console.log('moduleGroup is not set.');
    }
    if (!module) {
      console.log('module is not set.');
    }

    if (module.isNew) {
      module.isNew = false;
      moduleGroup.modules.push(module);
    } else {
      let index = -1;
      moduleGroup.modules.find((m, i) => {
        if (m.tempId === module.tempId) {
          index = i;
          return true;
        }
        return false;
      });
      if (index > -1) {
        moduleGroup.modules[index] = module;
      }
    }

    setShowAddModule(false);
  };

  // Render the edit form with the applicant data
  return (
    <div className='flex h-full'>
      <Sidebar
        action={action}
        courseId={courseId}
        setActiveSection={setActiveSection}
        activeSection={activeSection}
      />
      <div className='flex-1 overflow-y-auto'>
        <h2 className='text-uidarker px-6 pt-6 text-md relative flex items-center'>
          <Link className='text-kenticoblue underline' to='../courses'>
            Courses
          </Link>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='12'
            height='12'
            fill='currentColor'
            className='bi bi-chevron-right'
            viewBox='0 0 16 16'
          >
            <path
              fillRule='evenodd'
              d='M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708'
            />
          </svg>
          {action === 'edit' ? 'Edit' : 'Add'} Course
          {action === 'edit' ? (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='12'
              height='12'
              fill='currentColor'
              className='bi bi-chevron-right'
              viewBox='0 0 16 16'
            >
              <path
                fillRule='evenodd'
                d='M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708'
              />
            </svg>
          ) : (
            ''
          )}
          {action === 'edit' ? formData.courseTitle : ''}
        </h2>
        {activeSection === 'details' && (!courseId || course.courseId) && (
          <CourseDetails
            formData={course}
            register={register}
            control={control}
            errors={errors}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            setValue={setValue}
            watch={watch}
            isSubmitting={isSubmitting}
          />
        )}
        {activeSection === 'prices' && (
          <Prices
            formData={formData}
            register={register}
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            errors={errors}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            setFormData={setFormData}
            countries={countries}
            isSubmitting={isSubmitting}
          />
        )}
        {activeSection === 'modules' && (
          <>
            <Modules
              isSubmitting={isSubmitting}
              formData={formData}
              register={register}
              control={control}
              errors={errors}
              watch={watch}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
              handleLinkClickAddModuleGroup={handleLinkClickAddModuleGroup}
              handleLinkClickAddModule={handleLinkClickAddModule}
              setValue={setValue}
            />
            <Modal
              children={
                <AddModuleGroup
                  formData={formData}
                  moduleGroup={selectedModuleGroup}
                  onSaved={saveModuleGroup}
                />
              }
              isOpen={showAddModuleGroup}
              onClose={handleCloseModuleGroupModal}
              register={register}
            />
            <Modal
              children={
                <AddModule
                  formData={formData}
                  module={selectedModule}
                  moduleGroup={selectedModuleGroup}
                  onSaved={saveModule}
                  onClose={handleCloseModuleModal}
                  countries={countries}
                />
              }
              isOpen={showAddModule}
              onClose={handleCloseModuleModal}
            />
          </>
        )}
        {activeSection === 'entry-criteria' && (
          <EntryCriteria
            formData={formData}
            register={register}
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
          />
        )}
        {activeSection === 'access' && (
          <Access
            formData={formData}
            register={register}
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            courseId={courseId}
          />
        )}
        {activeSection === 'students' && (
          <Students
            formData={formData}
            register={register}
            control={control}
            errors={errors}
            handleSubmit={handleSubmit}
            courseId={courseId}
          />
        )}
        <Modal
          isOpen={showSuccessModal}
          onClose={handleCloseSuccessModal}
          className='max-w-xl'
        >
          <div className='p-6'>
            <h2 className='text-lg font-bold mb-4'>Success</h2>
            <p>The course has been successfully saved.</p>
            <div className='flex justify-end mt-4'>
              <Link to='/education-admin/courses'>
                <button className='bg-ui font-medium py-2 px-6 mr-3 rounded-full'>
                  Back to courses
                </button>
              </Link>
              <button
                onClick={handleCloseSuccessModal}
                className='bg-kenticoblue hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-full'
              >
                Continue editing
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AddCourse;